import React, { useState, useMemo, useRef } from "react";
import { cn } from "common/utils/utils";
import { Articles, ArticlesResponse } from "../../data/models/article";
import ArticleItem from "./articleItem";
import { Pagination } from "./Pagination";
import { ArticleShimmer } from "./articleShimmer";

interface TopArticlesProps {
  articles: Articles[];
  isLoading: boolean;
  totalPages: number;
  totalArticles: number;
  currentPageArticles: number;
  refetch: (page?: number) => Promise<ArticlesResponse>;
}

export const TopArticles: React.FC<TopArticlesProps> = ({
  articles,
  isLoading,
  totalPages,
  totalArticles,
  currentPageArticles,
  refetch,
}) => {
  const topArticlesRef = useRef<HTMLDivElement>(null);
  const tags = useMemo(() => {
    const tagSet = new Set<string>();
    articles.forEach((article) => {
      article.tags?.forEach((tag: any) => {
        if (tag.name !== "Fundraiser") tagSet.add(tag.name);
      });
    });
    return Array.from(tagSet);
  }, [articles]);

  const [selectedTag, setSelectedTag] = useState<string>("All");
  const [currentPage, setCurrentPage] = useState(1);

  const handlePageClick = async (pageNumber: number) => {
    setCurrentPage(pageNumber);
    await refetch(pageNumber);
    topArticlesRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const articlesByTag = useMemo(() => {
    const groups: Record<string, Articles[]> = {};
    tags.forEach((tag) => {
      groups[tag] = articles.filter((article) =>
        article.tags?.some((t: any) => t.name === tag),
      );
    });
    return groups;
  }, [articles, tags]);

  const articlesWithoutFundraiser = articles.filter(
    (article) => !article.tags?.some((tag: any) => tag.name === "Fundraiser")
  );

  const filteredArticles =
    selectedTag === "All"
      ? articlesWithoutFundraiser
      : (articlesByTag[selectedTag] || []).filter(
          (article) => !article.tags?.some((tag: any) => tag.name === "Fundraiser")
        );

  return (
    <div ref={topArticlesRef} className="bg-[#022928] py-12">
      <div className="mx-4 lg:mx-[100px]">
        <p className="font-inter text-[16px] font-semibold leading-[100%] tracking-[0%] text-white lg:text-[24px]">
          Filters
        </p>
        <div className="mb-8 mt-6 flex flex-wrap gap-3">
          <button
            className={cn(
              "rounded-full px-4 py-2 font-inter",
              selectedTag === "All"
                ? "bg-[#4FBA6D] text-[12px] font-semibold leading-[140%] tracking-[0%] text-white"
                : "bg-white text-[12px] font-semibold leading-[140%] tracking-[0%] text-[#022928]",
            )}
            onClick={() => setSelectedTag("All")}
          >
            All
          </button>
          {tags.map((tag) => (
            <button
              key={tag}
              className={cn(
                "rounded-full px-4 py-2 font-inter",
                selectedTag === tag
                  ? "bg-[#4FBA6D] text-white"
                  : "bg-white text-[#022928]",
              )}
              onClick={() => setSelectedTag(tag)}
            >
              {tag}
            </button>
          ))}
        </div>
        {/* Render articles grid */}
        <div className="hidden cursor-pointer grid-cols-3 gap-8 lg:grid">
          {isLoading ? (
            <>
              <ArticleShimmer />
              <ArticleShimmer />
              <ArticleShimmer />
            </>
          ) : (
            filteredArticles.map((article) => (
              <ArticleItem key={article.id} article={article} />
            ))
          )}
        </div>
        {/* Mobile horizontal scroll */}
        <div className="w-full overflow-x-auto pb-4 lg:hidden">
          <div className="flex w-full gap-4" style={{ minWidth: "100%" }}>
            {isLoading ? (
              <>
                <div className="min-w-[298px]">
                  <ArticleShimmer />
                </div>
                <div className="min-w-[298px]">
                  <ArticleShimmer />
                </div>
                <div className="min-w-[298px]">
                  <ArticleShimmer />
                </div>
              </>
            ) : (
              filteredArticles.map((article) => (
                <div className="min-w-[298px]" key={article.id}>
                  <ArticleItem article={article} />
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      {/* Pagination block */}
      {selectedTag === "All" && totalPages > 1 && (
        <div className="mt-8 flex justify-center">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageClick}
          />
        </div>
      )}
    </div>
  );
};
